import { IBasicUser } from "@faire/web-api--source/indigofair/data/IBasicUser";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IMessengerConversationParticipant } from "@faire/web-api--source/indigofair/data/IMessengerConversationParticipant";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useCallback, useMemo } from "react";

import {
  getConversationToken as getConversationModelToken,
  getConversationCreatedAt as getConversationModelCreatedAt,
  getConversationLatestMessage as getConversationModelLatestMessage,
  getConversationOtherPartyBrandToken as getConversationModelOtherPartyBrandToken,
  isConversationUnRead as isConversationModelUnRead,
  getConversationOtherPartyBusinessImage as getConversationModelOtherPartyBusinessImage,
  isConversationSupportConversation as isConversationModelSupportConversation,
  isConversationMarkedAsSpam as isConversationModelMarkedAsSpam,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  getConversationUserToken as getConversationModelUserToken,
  getConversationKey as getConversationModelKey,
  getConversationOrderedMessages as getConversationModelOrderedMessages,
  isConversationInputDisabled as isConversationModelInputDisabled,
  getConversationOtherPartyUserType as getConversationModelOtherPartyUserType,
  getConversationParticipants as getConversationModelParticipants,
  ConversationModel,
  useFetchAdditionalMessagesThrottled,
  useOpenConversation,
} from "./__internal__/ConversationModel";
import { MessageModel } from "./__internal__/MessageModel";
import { useMessengerContext } from "./__internal__/MessengerContext";
import { useMessengerStoreConversationIdentification } from "./__internal__/useMessengerStoreConversationIdentification";
import { useMessengerStoreConversationStorage } from "./__internal__/useMessengerStoreConversationStorage";
import { useMessengerStoreStoreUpdatedConversation } from "./__internal__/useMessengerStoreStoreUpdatedConversation";
import { isConversationToken } from "./utils";

export const useMessengerStoreGetConversation = (): {
  hasConversation: (conversationIdentifier: string) => boolean;
  getConversationToken: (conversationIdentifier: string) => string | undefined;
  getConversationCreatedAt: (
    conversationIdentifier: string
  ) => number | undefined;
  getConversationLatestMessage: (
    conversationIdentifier: string
  ) => MessageModel | undefined;
  getConversationRetailer: (
    conversationIdentifier: string
  ) => IGetMessengerConversationsResponse.ISimpleRetailer | undefined;
  getConversationBrand: (
    conversationIdentifier: string
  ) => IGetMessengerConversationsResponse.ISimpleBrand | undefined;
  getConversationOtherPartyType: (
    conversationIdentifier: string
  ) => keyof typeof IUser.Type | undefined;
  getConversationOtherPartyUserToken: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationOtherPartyRetailerToken: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationOtherPartyBrandToken: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationIsUnread: (conversationIdentifier: string) => boolean;
  getConversationOtherPartyFirstName: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationOtherPartyBusinessImage: (
    conversationIdentifier: string
  ) => IImage | undefined;
  getConversationIsSupportConversation: (
    conversationIdentifier: string
  ) => boolean;
  getConversationIsMarkedAsSpam: (conversationIdentifier: string) => boolean;
  getConversationIsStarredByBrand: (conversationIdentifier: string) => boolean;
  getConversationOtherPartyCompanyName: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationIdentifier: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationKey: (conversationIdentifier: string) => string | undefined;
  getConversationUserToken: (
    conversationIdentifier: string
  ) => string | undefined;
  getConversationOtherParty: (
    conversationIdentifier: string
  ) => IUser | undefined;
  getConversationOtherPartyProfileImage: (
    conversationIdentifier: string
  ) => IImage | undefined;
  getConversationOrderedMessages: (
    conversationIdentifier: string
  ) => MessageModel[] | undefined;
  getConversationIsInputDisabled: (
    conversationIdentifier: string
  ) => boolean | undefined;
  getConversationFetchAdditionalMessagesThrottled: (
    conversationIdentifier: string
  ) => (() => Promise<ConversationModel>) | undefined;
  getConversationOtherPartyUserType: (
    conversationIdentifier: string
  ) => IUser.Type | undefined;
  getConversationParticipants: (
    conversationIdentifier: string
  ) => IMessengerConversationParticipant[] | undefined;
  getConversationShouldShowLoadingSpinner: (
    conversationIdentifier: string
  ) => boolean;
  getConversationOpenConversation: (
    conversationIdentifier: string
  ) => (() => Promise<ConversationModel>) | undefined;
  getConversationMessageAuthors: (
    conversationIdentifier: string
  ) => IBasicUser[] | undefined;
  hasConversationWithRetailer: (retailerToken: string) => boolean;
  hasConversationWithBrand: (brandToken: string) => boolean;
  isConversationToken: (token: string) => boolean;
} => {
  const { strictLocalize } = useStrictLocalization();
  const { storeUpdatedConversation } =
    useMessengerStoreStoreUpdatedConversation();
  const { setTotalUnreadConversations } = useMessengerContext() ?? {};
  const { getConversationByIdentifier } =
    useMessengerStoreConversationIdentification();
  const { conversationByRetailerToken, conversationByBrandToken } =
    useMessengerStoreConversationStorage();

  const hasConversation = useCallback(
    (conversationIdentifier: string) => {
      return !!getConversationByIdentifier?.(conversationIdentifier);
    },
    [getConversationByIdentifier]
  );

  const getConversationToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? getConversationModelToken(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationCreatedAt = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelCreatedAt(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationLatestMessage = useCallback(
    (conversationIdentifier: string): MessageModel | undefined => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelLatestMessage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationRetailer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.retailer;
    },
    [getConversationByIdentifier]
  );

  const getConversationBrand = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.brand;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyType = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.type;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyUserToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyRetailerToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.retailer_token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBrandToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyBrandToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsUnread = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? isConversationModelUnRead(conversation) : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyFirstName = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.first_name;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBusinessImage = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyBusinessImage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsSupportConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelSupportConversation(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsMarkedAsSpam = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelMarkedAsSpam(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsStarredByBrand = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelStarredByBrand(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationUserToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelUserToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyCompanyName = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }

      let companyName = "";
      if (conversation.otherParty?.type === IUser.Type.ADMIN) {
        companyName = strictLocalize({
          id: "feVZJ0",
          defaultMessage: "Faire Support",
          description: {
            text: "Name of the other party when chatting with Faire Support",
          },
        });
      } else if (
        conversation.otherParty?.type === IUser.Type.USER &&
        conversation.retailer &&
        conversation.retailer.name
      ) {
        companyName = conversation.retailer.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.BRAND_USER &&
        conversation.brand &&
        conversation.brand.name
      ) {
        companyName = conversation.brand.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.GUEST &&
        conversation.otherPartyCompany
      ) {
        companyName = conversation.otherPartyCompany;
      }
      return companyName;
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getConversationIdentifier = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }

      return (
        getConversationModelToken(conversation) ??
        conversation.brand?.token ??
        conversation.retailer?.token ??
        getConversationModelUserToken(conversation)
      );
    },
    [getConversationByIdentifier]
  );

  const getConversationKey = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation ? getConversationModelKey(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherParty = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyProfileImage = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)?.otherParty
        ?.profile_image;
    },
    [getConversationByIdentifier]
  );

  const getConversationOrderedMessages = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOrderedMessages(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsInputDisabled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? isConversationModelInputDisabled(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const fetchAdditionalMessagesThrottled = useFetchAdditionalMessagesThrottled();

  const getConversationFetchAdditionalMessagesThrottled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      if (!conversation) {
        return undefined;
      }
      const fetchAdditionalMessagesThrottledCallback =
        fetchAdditionalMessagesThrottled(conversation);
      return async () => {
        const updatedConversation =
          await fetchAdditionalMessagesThrottledCallback();
        storeUpdatedConversation(updatedConversation);
        return updatedConversation;
      };
    },
    [fetchAdditionalMessagesThrottled, getConversationByIdentifier, storeUpdatedConversation]
  );

  const getConversationOtherPartyUserType = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelOtherPartyUserType(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationParticipants = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? getConversationModelParticipants(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationShouldShowLoadingSpinner = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation?.isLoading || !!conversation?.nextRequest;
    },
    [getConversationByIdentifier]
  );

  const openConversation = useOpenConversation();

  const getConversationOpenConversation = useCallback(
    (conversationIdentifier: string) => {
      if (!setTotalUnreadConversations) {
        return undefined;
      }

      const conversation = getConversationByIdentifier?.(
        conversationIdentifier
      );
      return conversation
        ? async () => {
            const [updatedConversation, totalUnreadConversations] =
              await openConversation(conversation);
            storeUpdatedConversation(updatedConversation);
            setTotalUnreadConversations(totalUnreadConversations);
            return updatedConversation;
          }
        : undefined;
    },
    [getConversationByIdentifier, openConversation, setTotalUnreadConversations, storeUpdatedConversation]
  );

  const getConversationMessageAuthors = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier?.(conversationIdentifier)
        ?.messageAuthors;
    },
    [getConversationByIdentifier]
  );

  const hasConversationWithRetailer = useCallback(
    (retailerToken: string) => {
      return !!conversationByRetailerToken?.get(retailerToken);
    },
    [conversationByRetailerToken]
  );

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken?.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({
      hasConversation,
      getConversationToken,
      getConversationCreatedAt,
      getConversationLatestMessage,
      getConversationRetailer,
      getConversationBrand,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyBrandToken,
      getConversationIsUnread,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyBusinessImage,
      getConversationIsSupportConversation,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationOtherPartyCompanyName,
      getConversationIdentifier,
      getConversationKey,
      getConversationOtherParty,
      getConversationUserToken,
      getConversationOtherPartyProfileImage,
      getConversationOrderedMessages,
      getConversationIsInputDisabled,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationShouldShowLoadingSpinner,
      getConversationOpenConversation,
      getConversationMessageAuthors,
      isConversationToken,
      hasConversationWithRetailer,
      hasConversationWithBrand,
    }),
    [
      getConversationToken,
      getConversationBrand,
      getConversationCreatedAt,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationIdentifier,
      getConversationIsInputDisabled,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationIsSupportConversation,
      getConversationIsUnread,
      getConversationKey,
      getConversationLatestMessage,
      getConversationMessageAuthors,
      getConversationOpenConversation,
      getConversationOrderedMessages,
      getConversationOtherParty,
      getConversationOtherPartyBrandToken,
      getConversationOtherPartyBusinessImage,
      getConversationOtherPartyCompanyName,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyProfileImage,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationRetailer,
      getConversationShouldShowLoadingSpinner,
      getConversationUserToken,
      hasConversation,
      hasConversationWithBrand,
      hasConversationWithRetailer,
    ]
  );
};
